import type {
  DrawingRevisionGroup,
  DrawingRevisionOption,
  PageClassification,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { preferUserEditValue } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { pageRevisionFamilyKey } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import {
  pageMetadataUserEditPageNum,
  selectLatestConfirmedPageMetadataUserEdit,
} from 'features/knowledgeDocumentViewer/utils/selectLatestConfirmedPageMetadataUserEdit'

export function pageUserEditLookupKey(
  documentId: string,
  pageNum: number
): string {
  return `${documentId}:${pageNum}`
}

/** Index latest-confirmed page user-edit values by `documentId:pageNum`. */
export function indexPageUserEditsByDocumentAndPage(
  edits: DocumentPageMetadataUserEditEntry[]
): Map<string, DocumentPageMetadataUserEditValues> {
  const grouped = new Map<string, DocumentPageMetadataUserEditEntry[]>()
  for (const edit of edits) {
    const pageNum = pageMetadataUserEditPageNum(edit)
    if (pageNum === undefined) continue
    const key = pageUserEditLookupKey(edit.entry.data.document_id, pageNum)
    const group = grouped.get(key)
    if (group) {
      group.push(edit)
    } else {
      grouped.set(key, [edit])
    }
  }
  const byDocAndPage = new Map<string, DocumentPageMetadataUserEditValues>()
  for (const [key, group] of grouped) {
    const latest = selectLatestConfirmedPageMetadataUserEdit(group)
    const values = latest?.entry.data.values
    if (values) {
      byDocAndPage.set(key, values)
    }
  }
  return byDocAndPage
}

export function applyPageUserEditToPageClassification(
  classification: PageClassification,
  editValues: DocumentPageMetadataUserEditValues | undefined
): PageClassification {
  if (editValues === undefined) {
    return classification
  }
  return {
    ...classification,
    category: preferUserEditValue(
      editValues.page_classification,
      classification.category
    ),
    drawing_number: preferUserEditValue(
      editValues.issuer_drawing_number,
      classification.drawing_number
    ),
    sheet_number: preferUserEditValue(
      editValues.sheet_number,
      classification.sheet_number
    ),
    revision_code: preferUserEditValue(
      editValues.latest_revision,
      classification.revision_code
    ),
    revision_date: preferUserEditValue(
      editValues.latest_revision_date,
      classification.revision_date
    ),
  }
}

export function applyPageUserEditsToClassificationsMap(
  classificationsMap: Map<string, PageClassification[]>,
  editsByDocAndPage: Map<string, DocumentPageMetadataUserEditValues>
): Map<string, PageClassification[]> {
  const next = new Map<string, PageClassification[]>()
  for (const [documentId, classifications] of classificationsMap) {
    next.set(
      documentId,
      classifications.map((classification) =>
        applyPageUserEditToPageClassification(
          classification,
          editsByDocAndPage.get(
            pageUserEditLookupKey(documentId, classification.page_num)
          )
        )
      )
    )
  }
  return next
}

function resolveGroupDrawingNumber(
  editValues: DocumentPageMetadataUserEditValues | undefined,
  generatedDrawingNumber: string
): string {
  const next = preferUserEditValue(
    editValues?.issuer_drawing_number,
    generatedDrawingNumber
  )
  if (next === undefined) {
    return generatedDrawingNumber
  }
  return next ?? ''
}

function optionMatchesRevisionFamilyKey(
  option: DrawingRevisionOption,
  familyKey: string | undefined,
  groupPageNum: number
): boolean {
  if (!familyKey) return false
  const classification =
    option.page_classifications?.find(
      (entry) => entry.page_num === groupPageNum
    ) ?? option.page_classifications?.[0]
  const optionKey = pageRevisionFamilyKey(
    classification?.drawing_number,
    classification?.sheet_number
  )
  return optionKey === familyKey
}

export function applyPageUserEditsToDrawingRevisionGroupsMap(
  groupsMap: Map<string, DrawingRevisionGroup[]>,
  editsByDocAndPage: Map<string, DocumentPageMetadataUserEditValues>
): Map<string, DrawingRevisionGroup[]> {
  const next = new Map<string, DrawingRevisionGroup[]>()
  for (const [hostDocumentId, groups] of groupsMap) {
    next.set(
      hostDocumentId,
      groups.map((group) => {
        const hostEdit = editsByDocAndPage.get(
          pageUserEditLookupKey(hostDocumentId, group.page_num)
        )
        const drawingNumber = resolveGroupDrawingNumber(
          hostEdit,
          group.drawing_number
        )
        const sheetNumber = preferUserEditValue(
          hostEdit?.sheet_number,
          group.sheet_number
        )
        const familyKey = pageRevisionFamilyKey(drawingNumber, sheetNumber)
        const options = group.options
          .map((option) => ({
            ...option,
            page_classifications: option.page_classifications?.map(
              (classification) =>
                applyPageUserEditToPageClassification(
                  classification,
                  editsByDocAndPage.get(
                    pageUserEditLookupKey(
                      option.document_id,
                      classification.page_num
                    )
                  )
                )
            ),
          }))
          .filter((option) =>
            optionMatchesRevisionFamilyKey(option, familyKey, group.page_num)
          )
        return {
          ...group,
          drawing_number: drawingNumber,
          sheet_number: sheetNumber,
          options,
        }
      })
    )
  }
  return next
}
